import React from 'react';
import Chip from '@mui/material/Chip';
import { differenceInCalendarDays } from 'date-fns';

export const createOrderSchema = ({
  formatDate,
  formatProduceNames,
  computeProduceTotal,
  getDeliveryPrice,
  getDeliveryCurrency,
  getProduceCurrency,
  toDate,
  variant = 'dashboard',
}) => {
  const includeTimeUntil = variant === 'dashboard';
  const includeSubtotal = variant === 'full';
  const includeRecurring = variant === 'full';

  const summaryFields = [
    {
      key: 'orderNumber',
      label: 'Order Number',
      format: (val) => (val != null && val !== '' ? `#${val}` : ''),
    },
    { key: 'customerName', label: 'Customer' },
    {
      key: 'deliveryDate',
      label: 'Delivery Date',
      format: (_, row) => {
        const d = toDate(row?.deliveryDate);
        return d ? <Chip label={formatDate(row.deliveryDate)} size="small" variant="outlined" /> : '';
      },
    },
  ];

  if (includeTimeUntil) {
    summaryFields.push({
      key: 'timeUntil',
      label: 'Time Until',
      format: (_, row) => {
        const d = toDate(row?.deliveryDate);
        if (!d) return '';
        const daysDiff = differenceInCalendarDays(d, new Date());
        if (daysDiff < 0) {
          return <Chip label={`${Math.abs(daysDiff)} days overdue`} color="warning" size="small" />;
        }
        if (daysDiff === 0) return <Chip label="Today" color="secondary" size="small" />;
        return <Chip label={`${daysDiff} days`} size="small" />;
      },
    });
  }

  if (includeSubtotal) {
    summaryFields.push({
      key: 'subtotal',
      label: 'Subtotal',
      format: (_, row) => {
        const currency = getProduceCurrency ? getProduceCurrency(row) : getDeliveryCurrency(row);
        return (
          <Chip
            label={`${currency}${computeProduceTotal(row).toFixed(2)}`}
            variant="outlined"
            size="small"
          />
        );
      },
    });
  }

  summaryFields.push({
    key: 'total',
    label: 'Total',
    format: (_, row) => {
      const total = computeProduceTotal(row) + getDeliveryPrice(row);
      return (
        <Chip
          label={`${getDeliveryCurrency(row)}${total.toFixed(2)}`}
          size="small"
          sx={(theme) => ({
            backgroundColor: theme.palette.success.dark,
            color: theme.palette.getContrastText(theme.palette.success.dark),
          })}
        />
      );
    },
  });

  if (includeRecurring) {
    summaryFields.push({
      key: 'recurring',
      label: 'Recurring',
      format: (val) => {
        if (!val) return 'One-time';
        if (typeof val === 'object') {
          const { frequency, period } = val;
          return frequency ? `Every ${frequency} ${period}${Number(frequency) > 1 ? 's' : ''}` : 'One-time';
        }
        if (typeof val === 'string') return val.charAt(0).toUpperCase() + val.slice(1);
        return 'One-time';
      },
    });
  }

  return {
    title: 'Order Details',
    summary: {
      title: 'Summary',
      fields: summaryFields,
    },
    sections: [
      {
        title: 'Produce',
        fields: [
          {
            key: 'produce',
            label: 'Produce',
            format: (_, row) => formatProduceNames(row),
          },
        ],
      },
      {
        title: 'Notes',
        fields: [
          { key: 'notes', label: 'Notes' },
        ],
      },
    ],
  };
};
